import React, { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Mail, Phone, MapPin, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPage() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    da: {
      title: "Privatlivspolitik",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Senest opdateret: 24. juni 2026",
      intro: "Hos Vejle AutoExport (CVR: 30158628) prioriterer vi beskyttelsen af dine personlige data højt. Denne privatlivspolitik forklarer, hvordan vi indsamler, behandler og beskytter dine personoplysninger, når du besøger vores hjemmeside eller benytter vores tjenester til vurdering og salg af leasingbiler samt eksportbiler.",
      sections: [
        {
          title: "1. Dataansvarlig",
          body: "Vejle AutoExport er dataansvarlig for behandlingen af dine personoplysninger. Du kan kontakte os på:",
          contact: {
            email: "info@vejleautoexport.dk",
            phone: "+45 60 13 34 73",
            address: "Storgaden 97, 6052 Viuf"
          }
        },
        {
          title: "2. Hvilke oplysninger indsamler vi?",
          body: "Vi indsamler og behandler kun de oplysninger, der er nødvendige for at kunne vurdere din bil og kontakte dig med et tilbud. Dette inkluderer:\n\n• Navn, e-mailadresse og telefonnummer.\n• Bilens registreringsnummer (nummerplade), mærke, model, variant, kilometerstand, stand, billeder samt leasing- og finansieringsoplysninger."
        },
        {
          title: "3. Formål og retsgrundlag",
          body: "Behandlingen af dine personoplysninger sker med det formål at:\n\n• Udarbejde en gratis og uforpligtende markedsvurdering af din bil.\n• Kontakte dig med henblik på et eventuelt køb eller rådgivning.\n• Overholde gældende lovgivning, herunder hvidvaskloven og bogføringsloven ved gennemførelse af en handel.\n\nRetsgrundlaget for behandlingen er opfyldelse af en kontrakt eller forberedelse heraf (databeskyttelsesforordningens art. 6, stk. 1, litra b) samt vores legitime interesse i at besvare dine henvendelser (art. 6, stk. 1, litra f)."
        },
        {
          title: "4. Videregivelse af oplysninger",
          body: "Vi videregiver ikke dine personoplysninger til tredjemand, medmindre det er nødvendigt for at opfylde vores aftale med dig (f.eks. i forbindelse med afhentning af bilen) eller for at overholde lovmæssige krav. Vi anvender betroede databehandlere (f.eks. IT-leverandører), som udelukkende behandler data efter vores instruks."
        },
        {
          title: "5. Opbevaring og sletning",
          body: "Vi opbevarer dine personoplysninger, så længe det er nødvendigt til de formål, de er indsamlet til. Hvis en handel gennemføres, opbevarer vi relevante oplysninger i 5 år i henhold til bogføringsloven. Hvis der ikke indgås en aftale, slettes dine data senest efter 12 måneder, medmindre du har givet samtykke til længere opbevaring."
        },
        {
          title: "6. Dine rettigheder",
          body: "Du har efter databeskyttelsesforordningen en række rettigheder i forhold til vores behandling af dine oplysninger:\n\n• Ret til indsigt i de oplysninger, vi behandler om dig.\n• Ret til at få berigtiget (rettet) forkerte oplysninger.\n• Ret til at blive slettet (\"retten til at blive glemt\") i visse tilfælde.\n• Ret til at begrænse behandlingen af dine data.\n• Ret til at gøre indsigelse mod vores behandling.\n\nDu kan udøve dine rettigheder ved at kontakte os på info@vejleautoexport.dk. Du har også ret til at indgive en klage til Datatilsynet."
        }
      ],
      backBtn: "Tilbage til forsiden"
    },
    en: {
      title: "Privacy Policy",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Last updated: June 24, 2026",
      intro: "At Vejle AutoExport (CVR: 30158628), we highly prioritize the protection of your personal data. This privacy policy explains how we collect, process, and protect your personal data when you visit our website or use our services for valuation, sale of leasing cars, and export cars.",
      sections: [
        {
          title: "1. Data Controller",
          body: "Vejle AutoExport is the data controller for the processing of your personal data. You can contact us at:",
          contact: {
            email: "info@vejleautoexport.dk",
            phone: "+45 60 13 34 73",
            address: "Storgaden 97, 6052 Viuf, Denmark"
          }
        },
        {
          title: "2. What information do we collect?",
          body: "We only collect and process information necessary to evaluate your car and contact you with an offer. This includes:\n\n• Name, email address, and phone number.\n• Car registration number (license plate), brand, model, variant, mileage, condition, images, and leasing/financing details."
        },
        {
          title: "3. Purpose and legal basis",
          body: "The processing of your personal data is done for the purpose of:\n\n• Preparing a free and non-binding market valuation of your car.\n• Contacting you regarding a potential purchase or advice.\n• Complying with applicable legislation, including the Anti-Money Laundering Act and the Bookkeeping Act when completing a transaction.\n\nThe legal basis for processing is the performance of a contract or preparation thereof (GDPR Art. 6(1)(b)) and our legitimate interest in responding to your inquiries (Art. 6(1)(f))."
        },
        {
          title: "4. Disclosure of information",
          body: "We do not disclose your personal data to third parties unless necessary to fulfill our agreement with you (e.g., in connection with car pickup) or to comply with legal requirements. We use trusted data processors (e.g., IT providers) who process data solely on our instructions."
        },
        {
          title: "5. Storage and deletion",
          body: "We store your personal data as long as necessary for the purposes for which they were collected. If a transaction is completed, we store relevant information for 5 years in accordance with the Bookkeeping Act. If no agreement is reached, your data is deleted after 12 months at the latest, unless you have consented to longer storage."
        },
        {
          title: "6. Your rights",
          body: "Under the GDPR, you have several rights regarding our processing of your data:\n\n• Right of access to the data we process about you.\n• Right to rectification (correction) of incorrect data.\n• Right to erasure (\"right to be forgotten\") in certain cases.\n• Right to restrict the processing of your data.\n• Right to object to our processing.\n\nYou can exercise your rights by contacting us at info@vejleautoexport.dk. You also have the right to lodge a complaint with the Danish Data Protection Agency (Datatilsynet)."
        }
      ],
      backBtn: "Back to home"
    },
    de: {
      title: "Datenschutzerklärung",
      subtitle: "Vejle AutoExport",
      lastUpdated: "Zuletzt aktualisiert: 24. Juni 2026",
      intro: "Bei Vejle AutoExport (CVR: 30158628) legen wir großen Wert auf den Schutz Ihrer persönlichen Daten. Diese Datenschutzerklärung erklärt, wie wir Ihre personenbezogenen Daten erheben, verarbeiten und schützen, wenn Sie unsere Website besuchen oder unsere Dienstleistungen zur Bewertung und zum Verkauf von Leasing- und Exportfahrzeugen nutzen.",
      sections: [
        {
          title: "1. Verantwortlicher für die Datenverarbeitung",
          body: "Vejle AutoExport ist der Verantwortliche für die Verarbeitung Ihrer personenbezogenen Daten. Sie können uns kontaktieren unter:",
          contact: {
            email: "info@vejleautoexport.dk",
            phone: "+45 60 13 34 73",
            address: "Storgaden 97, 6052 Viuf, Dänemark"
          }
        },
        {
          title: "2. Welche Daten erfassen wir?",
          body: "Wir erheben und verarbeiten nur Daten, die für die Bewertung Ihres Fahrzeugs und die Kontaktaufnahme mit einem Angebot erforderlich sind. Dies umfasst:\n\n• Name, E-Mail-Adresse und Telefonnummer.\n• Kfz-Kennzeichen (optional), Marke, Modell, Variante, Kilometerstand, Zustand, Bilder sowie Leasing- und Finanzierungsdetails."
        },
        {
          title: "3. Zweck und Rechtsgrundlage",
          body: "Die Verarbeitung Ihrer personenbezogenen Daten erfolgt zu folgenden Zwecken:\n\n• Erstellung einer kostenlosen und unverbindlichen Marktbewertung Ihres Fahrzeugs.\n• Kontaktaufnahme zwecks eines eventuellen Kaufs oder Beratung.\n• Einhaltung geltender Gesetze, einschließlich des Geldwäschegesetzes und des Buchführungsgesetzes bei Durchführung einer Transaktion.\n\nDie Rechtsgrundlage für die Verarbeitung ist die Erfüllung eines Vertrags oder dessen Anbahnung (DSGVO Art. 6 Abs. 1 lit. b) sowie unser berechtigtes Interesse an der Beantwortung Ihrer Anfragen (Art. 6 Abs. 1 lit. f)."
        },
        {
          title: "4. Weitergabe von Daten",
          body: "Wir geben Ihre personenbezogenen Daten nicht an Dritte weiter, es sei denn, dies ist zur Erfüllung unserer Vereinbarung mit Ihnen erforderlich (z. B. im Zusammenhang mit der Fahrzeugabholung) oder um gesetzliche Anforderungen zu erfüllen. Wir setzen vertrauenswürdige Auftragsverarbeiter (z. B. IT-Dienstleister) ein, die Daten ausschließlich nach unseren Weisungen verarbeiten."
        },
        {
          title: "5. Speicherung und Löschung",
          body: "Wir speichern Ihre personenbezogenen Daten so lange, wie es für die Zwecke, für die sie erhoben wurden, erforderlich ist. Wenn eine Transaktion abgeschlossen wird, bewahren wir relevante Informationen gemäß dem dänischen Buchführungsgesetz 5 Jahre lang auf. Wenn keine Vereinbarung zustande kommt, werden Ihre Daten spätestens nach 12 Monaten gelöscht, es sei denn, Sie haben einer längeren Speicherung zugestimmt."
        },
        {
          title: "6. Ihre Rechte",
          body: "Nach der DSGVO haben Sie verschiedene Rechte bezüglich unserer Verarbeitung Ihrer Daten:\n\n• Recht auf Auskunft über die von uns verarbeiteten Daten.\n• Recht auf Berichtigung unrichtiger Daten.\n• Recht auf Löschung („Recht auf Vergessenwerden“) in bestimmten Fällen.\n• Recht auf Einschränkung der Verarbeitung Ihrer Daten.\n• Recht auf Widerspruch gegen unsere Verarbeitung.\n\nSie können Ihre Rechte ausüben, indem Sie uns unter info@vejleautoexport.dk kontaktieren. Sie haben auch das Recht, eine Beschwerde bei der dänischen Datenschutzbehörde (Datatilsynet) einzureichen."
        }
      ],
      backBtn: "Zurück zur Startseite"
    }
  };

  const currentContent = content[language] || content.da;

  return (
    <div className="pt-36 md:pt-44 pb-24 bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#293e30] font-bold text-xs uppercase tracking-wider mb-8 transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          {currentContent.backBtn}
        </button>

        {/* Main Card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 md:p-12 space-y-10">
          {/* Header */}
          <div className="border-b border-slate-100 pb-8">
            <div className="flex items-center gap-3 text-[#293e30] mb-3">
              <Shield className="h-8 w-8" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                {currentContent.subtitle}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black italic text-[#293e30] uppercase tracking-tight">
              {currentContent.title}
            </h1>
            <p className="text-xs text-slate-400 mt-2 font-medium">
              {currentContent.lastUpdated}
            </p>
          </div>

          {/* Intro */}
          <p className="text-slate-600 leading-relaxed font-medium text-base">
            {currentContent.intro}
          </p>

          {/* Sections */}
          <div className="space-y-8">
            {currentContent.sections.map((section, idx) => (
              <div key={idx} className="space-y-3">
                <h2 className="text-lg font-bold text-[#293e30] uppercase tracking-wide">
                  {section.title}
                </h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line text-sm">
                  {section.body}
                </p>
                
                {section.contact && (
                  <div className="bg-slate-50 rounded-xl p-5 mt-4 border border-slate-100 space-y-3 text-sm">
                    <div className="flex items-center gap-3 text-slate-700">
                      <Mail className="h-4 w-4 text-[#293e30] shrink-0" />
                      <a href={`mailto:${section.contact.email}`} className="hover:text-[#293e30] transition-colors font-medium">
                        {section.contact.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <Phone className="h-4 w-4 text-[#293e30] shrink-0" />
                      <a href={`tel:${section.contact.phone.replace(/\s+/g, '')}`} className="hover:text-[#293e30] transition-colors font-medium">
                        {section.contact.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <MapPin className="h-4 w-4 text-[#293e30] shrink-0" />
                      <span className="font-medium">{section.contact.address}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
